// AGC: MapLocationPicker - Interactive map for selecting farm location
// AGC: Uses Google Maps (loaded via useJsApiLoader) to allow point selection

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useJsApiLoader } from '@react-google-maps/api'
import { GOOGLE_MAPS_CONFIG } from '@droidfarm/shared'
import { MapPin, Loader2, Navigation, X } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { PermissionBlockedError } from '../../services/nativeGeolocation'
import toast from 'react-hot-toast'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface MapLocationPickerProps {
  countryCode: string // AGC: 'BR' or 'AR'
  initialCenter?: [number, number]
  onLocationSelect: (location: {
    lat: number
    lng: number
    address: string
  }) => void
  onClose: () => void
}

// AGC: Country center coordinates
const COUNTRY_CENTERS: Record<string, [number, number]> = {
  'BR': [-15.7975, -47.8919], // AGC: Brasilia
  'AR': [-34.6037, -58.3816], // AGC: Buenos Aires
}

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  countryCode,
  initialCenter,
  onLocationSelect,
  onClose,
}) => {
  const { t } = useTranslation()

  // AGC: Load Google Maps JS API via useJsApiLoader (same as ManualPolygonGeometry)
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [marker, setMarker] = useState<google.maps.Marker | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [address, setAddress] = useState<string>('')
  const [isGettingAddress, setIsGettingAddress] = useState(false)
  const [showPermBlocked, setShowPermBlocked] = useState(false)

  const mapRef = useRef<HTMLDivElement>(null)
  // AGC: Ref to always hold latest click handler (avoids stale closure in Google Maps listener)
  const handleClickRef = useRef<(latLng: google.maps.LatLng) => void>(() => {})

  // AGC: Reverse geocode to get address
  const reverseGeocode = useCallback(async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { headers: { 'User-Agent': 'DroidFarm/1.0' } }
      )
      if (!response.ok) throw new Error('Geocoding failed')
      const data = await response.json()
      const city = data.address.city || data.address.town || data.address.village || ''
      const state = data.address.state || ''
      const country = data.address.country || ''
      return `${city}${city && state ? ', ' : ''}${state}${(city || state) && country ? ', ' : ''}${country}`
    } catch (error) {
      console.error('AGC: Error reverse geocoding:', error)
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    }
  }, [])

  // AGC: Handle map click — uses map/marker from state
  const handleMapClick = useCallback(async (latLng: google.maps.LatLng) => {
    if (!map) return
    const lat = latLng.lat()
    const lng = latLng.lng()

    if (marker) {
      marker.setPosition(latLng)
    } else {
      const newMarker = new google.maps.Marker({
        position: latLng,
        map: map,
        draggable: true,
        animation: google.maps.Animation.DROP,
      })
      // AGC: Drag listener uses ref to avoid stale closure
      newMarker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) handleClickRef.current(e.latLng)
      })
      setMarker(newMarker)
    }

    setSelectedLocation({ lat, lng })
    setIsGettingAddress(true)
    const locationAddress = await reverseGeocode(lat, lng)
    setAddress(locationAddress)
    setIsGettingAddress(false)
  }, [map, marker, reverseGeocode])

  // AGC: Keep ref in sync so Google Maps listeners always call latest handler
  useEffect(() => {
    handleClickRef.current = handleMapClick
  }, [handleMapClick])

  // AGC: Create map instance once API is loaded
  useEffect(() => {
    if (!isLoaded || loadError || !mapRef.current) return

    try {
      const center = initialCenter || COUNTRY_CENTERS[countryCode] || COUNTRY_CENTERS['BR']
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: { lat: center[0], lng: center[1] },
        zoom: initialCenter ? 14 : 6,
        mapTypeId: 'satellite',
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_RIGHT,
        },
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      })

      // AGC: Click listener delegates through ref — always calls latest handleMapClick
      mapInstance.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) handleClickRef.current(e.latLng)
      })

      setMap(mapInstance)
    } catch (error) {
      console.error('AGC: Error initializing map:', error)
      toast.error(t('wizard.location.mapsLoadError'))
    }
  }, [isLoaded, loadError, countryCode, initialCenter])

  // AGC: Use current GPS location
  const handleUseCurrentLocation = useCallback(async () => {
    if (!map) return
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      const { latitude, longitude } = coords
      const latLng = new google.maps.LatLng(latitude, longitude)
      map.setCenter(latLng)
      map.setZoom(14)
      handleMapClick(latLng)
      toast.success(t('wizard.location.gpsSuccess'))
    } catch (err: any) {
      console.error('AGC: Error getting GPS:', err)
      if (err instanceof PermissionBlockedError) {
        setShowPermBlocked(true)
      } else {
        toast.error(err?.message || t('wizard.location.gpsError'))
      }
    }
  }, [map, handleMapClick, t])
  
  const handleConfirm = () => {
    if (selectedLocation && address) {
      onLocationSelect({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        address,
      })
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* AGC: Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('wizard.location.searchMap')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('wizard.location.selectOnMap')}
          </p>
        </div>
        
        {/* AGC: Map Container */}
        <div className="flex-1 relative">
          {!isLoaded && !loadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
              </div>
            </div>
          )}
          {loadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
              <div className="text-center px-4">
                <X className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{t('wizard.location.mapsLoadError')}</p>
              </div>
            </div>
          )}

          <div ref={mapRef} className="w-full h-full min-h-[400px]" />
          
          {/* AGC: GPS Button Overlay */}
          <button
            onClick={handleUseCurrentLocation}
            className="absolute bottom-4 left-4 px-4 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <Navigation className="w-5 h-5" />
            {t('wizard.location.useGPS')}
          </button>
        </div>
        
        {/* AGC: Selected Location Info */}
        {selectedLocation && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('wizard.location.title')}
                </p>
                {isGettingAddress ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('wizard.location.searching')}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {address}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* AGC: Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedLocation || isGettingAddress}
            className="flex-1 px-4 py-3 bg-[var(--accent-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>

      {/* AGC: Blocked permission modal */}
      <PermissionBlockedModal
        isOpen={showPermBlocked}
        permissionType="location"
        onClose={() => setShowPermBlocked(false)}
      />
    </div>
  )
}

export default MapLocationPicker
